/* eslint-disable no-undef */
import React from 'react';
import PropTypes from 'prop-types';
import withBoundary from 'component/ErrorBoundary/withBoundary';
import {
  ACTION_SHORTCUT_TRIGGERED,
  VIDEO_MUTE,
  createShortcutEvent
} from 'store/modules/analytics/AnalyticsEvents';
import { sendAnalytics } from 'store/modules/analytics/functions';
import AbstractButton from './AbstractButton';
import { handleToggleVideoMuted } from 'store/modules/toolbox/actions';
import { isVideoMuteButtonDisabled } from 'store/modules/toolbox/functions';
import { isLocalTrackMuted } from 'store/modules/base/tracks/functions';
import { MEDIA_TYPE } from 'store/modules/base/media/constants';
import { connect } from 'store/modules/base/redux/functions';
import { translate } from 'store/modules/base/i18n/functions';
import { VideoCam, VideoOff } from 'assets/images';
import {
  registerShortcut,
  unregisterShortcut
} from 'store/modules/keyboard-shortcuts/actions';

/**
 * Component that renders a toolbar button for toggling video mute.
 *
 * @augments AbstractVideoMuteButton
 */
class VideoMuteButton extends AbstractButton {
  accessibilityLabel = 'toolbar.accessibilityLabel.videomute';
  label = 'toolbar.videomute';
  icon = VideoCam;
  toggledIcon = VideoOff;

  // tooltip = 'toolbar.videomute';

  /**
   * Initializes a new {@code VideoMuteButton} instance.
   *
   * @param {Props} props - The read-only React {@code Component} props with
   * which the new instance is to be initialized.
   */
  constructor(props) {
    super(props);

    // Bind event handlers so they are only bound once per instance.
    this._onKeyboardShortcut = this._onKeyboardShortcut.bind(this);
  }

  /**
   * Registers the keyboard shortcut that toggles the video muting.
   *
   * @inheritdoc
   * @returns {void}
   */
  componentDidMount() {
    this.props.dispatch(
      registerShortcut({
        character: 'D',
        helpDescription: 'keyboardShortcuts.videoMute',
        handler: this._onKeyboardShortcut
      })
    );
  }

  /**
   * Unregisters the keyboard shortcut that toggles the video muting.
   *
   * @inheritdoc
   * @returns {void}
   */
  componentWillUnmount() {
    this.props.dispatch(unregisterShortcut('D'));
  }

  /**
   * Indicates if video is currently disabled or not.
   *
   * @override
   * @protected
   * @returns {boolean}
   */
  _isDisabled() {
    return this.props._videoDisabled;
  }

  /**
   * Indicates if video is currently muted or not.
   *
   * @override
   * @protected
   * @returns {boolean}
   */
  _isVideoMuted() {
    return this.props._videoMuted;
  }

  /**
   * Creates an analytics keyboard shortcut event and dispatches an action to
   * toggle the video muting.
   *
   * @private
   * @returns {void}
   */
  _onKeyboardShortcut() {
    // Ignore keyboard shortcuts if the video button is disabled.
    if (this._isDisabled()) {
      return;
    }

    sendAnalytics(
      createShortcutEvent(VIDEO_MUTE, ACTION_SHORTCUT_TRIGGERED, {
        enable: !this._isVideoMuted()
      })
    );

    AbstractButton.prototype._onClick.call(this);
  }

  /**
   * Changes the muted state.
   *
   * @override
   * @param {boolean} videoMuted - Whether video should be muted or not.
   * @protected
   * @returns {void}
   */
  _setVideoMuted(videoMuted) {
    this.props.dispatch(handleToggleVideoMuted(videoMuted, true, true));
  }

  /**
   * Handles clicking / pressing the button, and toggles the video mute state
   * accordingly.
   *
   * @protected
   * @returns {void}
   */
  _handleClick() {
    this._setVideoMuted(!this._isVideoMuted());
  }

  /**
   * Indicates whether this button is in toggled state or not.
   *
   * @override
   * @protected
   * @returns {boolean}
   */
  _isToggled() {
    return this._isVideoMuted();
  }
}

/**
 * Maps (parts of) the redux state to the associated props for the
 * {@code VideoMuteButton} component.
 *
 * @param {Object} state - The Redux state.
 * @private
 * @returns {{
 *     _videoMuted: boolean
 * }}
 */
function _mapStateToProps(state) {
  const tracks = state['features/base/tracks'];
  return {
    _videoDisabled: isVideoMuteButtonDisabled(state),
    _videoMuted: isLocalTrackMuted(tracks, MEDIA_TYPE.VIDEO),
    _userIdleMode: state['features/settings'].userIdleMode
  };
}
VideoMuteButton.defaultProps = {
  style: {
    display: 'flex'
  },
  isSwitchButton: true
};

/**
 * The type of the React {@code Component} props of {@link VideoMuteButton}.
 */
VideoMuteButton.propTypes = {
  /**
   * Whether video is currently muted or not.
   */
  _videoMuted: PropTypes.bool,

  /**
   * Whether video button is disabled or not.
   */
  _videoDisabled: PropTypes.bool,

  /**
   * The redux {@code dispatch} function.
   */
  dispatch: PropTypes.func
};
export default React.memo(
  translate(connect(_mapStateToProps)(withBoundary(VideoMuteButton)))
);
